<template>
  <section>
    <section class="preview-link">
      <div v-if="travelGroup" class="preview-title">
        <div>
          <p>{{ travelGroup.code }}</p>
          <p>{{ formatDate(travelGroup.departure_date) }}</p>
          <div v-html="travelGroup.name" style="margin: 0 auto;" />
        </div>

        <div>
          <b-button
            type="is-warning"
            class="is-fullwidth mr-1"
            native-type="submit"
            @click="onExportWord"
          >
            匯出
          </b-button>
          <a
            :href="groupPreviewUrl(travelGroup)"
            target="_blank"
            class="button is-primary"
          >
            預覽
          </a>
        </div>
      </div>
    </section>

    <b-tabs
      v-if="travelGroup && area"
      v-model="activeTab"
      type="is-toggle"
      :expanded="false"
      :animated="false"
      class="boarder-tabs"
      destroyOnHide
    >
      <b-tab-item :label="$t('attributes.detail_info')" :disabled="loading">
        <basic-form
          :travel-group="travelGroup"
          :situation-types="situationTypes"
          :type="type"
          @updated="onUpdated"
          @deleted="onDeleted"
          @deprecated="onDeprecated"
        />
      </b-tab-item>

      <b-tab-item label="複寫旅行團" :disabled="loading">
        <override-form :travel-group="travelGroup" />
      </b-tab-item>

      <b-tab-item label="售價設定" :disabled="loading">
        <cost-form :travel-group="travelGroup" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item label="售價進階(旅客類型)" :disabled="loading">
        <traveler-type-list :travel-group="travelGroup" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item label="售價進階(方案)" :disabled="loading">
        <plan-list :travel-group="travelGroup" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item label="團費說明" :disabled="loading">
        <cost-desc-form :travel-group="travelGroup" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item
        label="企業團資料"
        v-if="isFirmGroup(type)"
        :disabled="loading"
      >
        <collaborate-form :travel-group="travelGroup" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item
        label="企業團注意事項"
        v-if="isFirmGroup(type)"
        :disabled="loading"
      >
        <note-form :travel-group="travelGroup" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item
        label="企業團報名表"
        v-if="isFirmGroup(type)"
        :disabled="loading"
      >
        <common-questions :travel-group="travelGroup" />
      </b-tab-item>

      <b-tab-item label="自選行程" v-if="isFirmGroup(type)" :disabled="loading">
        <spot-questions :travel-group="travelGroup" />
      </b-tab-item>

      <b-tab-item label="圖檔設定" :disabled="loading">
        <pictures-form :travel-group="travelGroup" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item label="航班交通" :disabled="loading">
        <transfer-list :travel-group="travelGroup" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item label="交通說明" :disabled="loading">
        <traffic-form :travel-group="travelGroup" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item label="行程特色" :disabled="loading">
        <feature-form :travel-group="travelGroup" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item label="每日行程" :disabled="loading">
        <schedule-list
          :travel-group="travelGroup"
          :area="travelGroup.image_path"
          @updated="onUpdated"
        />
      </b-tab-item>

      <b-tab-item label="餐食住宿" :disabled="loading">
        <food-form :travel-group="travelGroup" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item label="優惠說明" :disabled="loading">
        <notes-form :travel-group="travelGroup" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item label="分類" :disabled="loading">
        <catalogs-form
          :travel-group="travelGroup"
          :catalogs="area_catalogs"
          @updated="onUpdated"
        />
      </b-tab-item>

      <b-tab-item label="內頁區塊" :disabled="loading">
        <block-list :travel-group="travelGroup" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item
        label="名額設定"
        v-if="isNormalGroup(type)"
        :disabled="loading"
      >
        <quantity-form :travel-group="travelGroup" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item :label="$t('attributes.country_flag')" :disabled="loading">
        <countries-form :travel-group="travelGroup" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item :label="$t('attributes.city_weather')" :disabled="loading">
        <weathers-form :travel-group="travelGroup" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item v-if="isNormalGroup(type)" :disabled="loading">
        <template #header>
          <span>
            首頁推薦
            <b-tag rounded>
              {{ area.promote_count }} / {{ area.promote_num }}
            </b-tag>
          </span>
        </template>
        <home-recommend :travel-group="travelGroup" @updated="onUpdated" />
      </b-tab-item>
      <b-tab-item v-if="isNormalGroup(type)" :disabled="loading">
        <template #header>
          <span>
            各館推薦
            <b-tag rounded>{{ area.hot_count }} / {{ area.hot_num }}</b-tag>
          </span>
        </template>
        <area-recommend :travel-group="travelGroup" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item
        label="蜜月推薦"
        v-if="isNormalGroup(type)"
        :disabled="loading"
      >
        <honey-moon-recommend
          :travel-group="travelGroup"
          @updated="onUpdated"
        />
      </b-tab-item>

      <b-tab-item :label="$t('attributes.seo')" :disabled="loading">
        <seo-form :travel-group="travelGroup" @updated="onUpdated" />
      </b-tab-item>

      <b-tab-item label="行程相簿" :disabled="loading">
        <blogs-form
          :travel-group="travelGroup"
          :catalog-list="travelGroup.catalog_list"
          @updated="onUpdated"
        />
      </b-tab-item>

      <b-tab-item label="航程管理" :disabled="loading">
        <voyage-list
          :travel-group="travelGroup"
          @updated="onUpdated"
        ></voyage-list>
      </b-tab-item>

      <b-loading :active.sync="loading" :is-full-page="false"></b-loading>
    </b-tabs>
  </section>
</template>

<script>
import { TRAVEL_GROUPS_API, AREA_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import BasicForm from './BasicForm'
import BlockList from './BlockList'
import CostForm from './CostForm'
import CostDescForm from './CostDescForm'
import QuantityForm from './QuantityForm'
import PicturesForm from './PicturesForm'
import CollaborateForm from './CollaborateForm'
import NoteForm from './NoteForm'
import CommonQuestions from './CommonQuestions'
import SpotQuestions from './SpotQuestions'
import FeatureForm from './FeatureForm'
import FoodForm from './FoodForm'
import TrafficForm from './TrafficForm'
import NotesForm from './NotesForm'
import CatalogsForm from './CatalogsForm'
import BlogsForm from './BlogsForm'
import SeoForm from './SeoForm'
import ScheduleList from './ScheduleList'
import TransferList from './TransferList'
import HomeRecommend from './HomeRecommend'
import AreaRecommend from './AreaRecommend'
import HoneyMoonRecommend from './HoneyMoonRecommend'
import CountriesForm from './CountriesForm'
import WeathersForm from './WeathersForm'
import OverrideForm from './OverrideForm'
import PlanList from './PlanList'
import TravelerTypeList from './TravelerTypeList'
import VoyageList from './VoyageList'

export default {
  name: 'TravelGroupEditForm',
  components: {
    VoyageList,
    BasicForm,
    BlockList,
    CostForm,
    CostDescForm,
    QuantityForm,
    PlanList,
    TravelerTypeList,
    PicturesForm,
    CollaborateForm,
    NoteForm,
    CommonQuestions,
    SpotQuestions,
    FeatureForm,
    FoodForm,
    TrafficForm,
    NotesForm,
    CatalogsForm,
    BlogsForm,
    SeoForm,
    ScheduleList,
    TransferList,
    HomeRecommend,
    AreaRecommend,
    HoneyMoonRecommend,
    CountriesForm,
    WeathersForm,
    OverrideForm
  },

  props: {
    type: {
      required: true,
      type: String,
      default: 'normal'
    },
    code: {
      required: true,
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      activeTab: 0,
      travelGroup: null,
      area: null,
      situationTypes: [],
      area_catalogs: []
    }
  },
  watch: {
    code(newCode) {
      this.loadTravelGroupAsync(newCode)
    }
  },
  mounted() {
    this.loadTravelGroupAsync(this.code)
  },
  methods: {
    async loadTravelGroupAsync(code) {
      try {
        this.$emit('update:loading', true)

        const res = await this.axios.get(`${TRAVEL_GROUPS_API}/${code}`)
        this.travelGroup = res.data.data
        const areaCode = this.travelGroup.area_code
        const [area_res, event_res, catalog_res] = await Promise.all([
          this.axios.get(`${AREA_API}/${areaCode}`),
          this.axios.get(`${AREA_API}/${areaCode}/events`),
          this.axios.get(`${AREA_API}/${areaCode}/catalogs`)
        ])

        this.area = area_res.data.data
        this.situationTypes = event_res.data.data
        this.area_catalogs = catalog_res.data.data
      } catch (e) {
        this.travelGroup = null
        this.area = null
        this.situationTypes = []
        this.area_catalogs = []

        notifyService.fetch_error('travel_group', e)
      } finally {
        this.$emit('update:loading', false)
      }
    },
    async loadAreaAsync(area_code) {
      try {
        const res = await this.axios.get(`${AREA_API}/${area_code}`)
        this.area = res.data.data
      } catch (e) {
        this.area = null

        notifyService.fetch_error('area', e)
      }
    },
    onUpdated(group) {
      this.travelGroup = group
      this.loadAreaAsync(group.area_code)

      this.$emit('updated', group)
    },
    onDeprecated(group) {
      this.travelGroup = group
      this.loadAreaAsync(group.area_code)

      this.$emit('deprecated', group)
    },
    onDeleted(code) {
      this.$emit('deleted', code)
    },
    async onExportWord() {
      try {
        const api = `${TRAVEL_GROUPS_API}/${this.travelGroup.code}/export`
        const type =
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        const fileName = `${this.travelGroup.name}.docx`

        const config = { headers: { Accept: type }, responseType: 'blob' }

        this.$emit('update:loading', true)

        const response = await this.axios.get(api, config)

        const blob = new Blob([response.data], { type: type })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        notifyService.error('匯出失敗', e)
      } finally {
        this.$emit('update:loading', false)
      }
    }
  }
}
</script>
